/**
 * CoachFeedbackList — plain-language guidance about what the coach's
 * uploaded example videos taught the scoring system
 * (backlog/coach-feedback-for-example-videos.md).
 *
 * Rendered inside CalibrationTakesPanel, ABOVE the raw `coach_messages`
 * list (which lives inside CalibrationStatusCard, now collapsed behind a
 * "Details" disclosure there — see that file). The two are not
 * duplicates: `coach_messages` describes calibration *state* ("2 correct
 * takes uploaded"); `coach_feedback` explains what those takes *taught*
 * the scoring system (e.g. "Add a Flawed take that's only mildly off —
 * your current ones are extreme, which teaches a wide grey zone").
 *
 * ── Staleness guard (red-team finding F3) ──────────────────────────────
 * The backend stamps `feedback_take_ids` (sorted) alongside `coach_feedback`
 * — the take-id set the feedback was computed from. This component compares
 * that against the *live* take list (passed in as `liveTakeIds`, sourced
 * from the same useCalibrationTakeList query CalibrationTakesPanel already
 * holds). On a mismatch, ALL item-level feedback is suppressed in favour of
 * a single "this feedback is stale" banner — the fail-closed calibration
 * worker can retain an old coach_feedback block referencing takes that have
 * since been deleted or added, and per-item text could describe a take that
 * no longer exists. `feedback_take_ids` absent (legacy calibrations) is
 * treated as fresh — there is nothing to compare against.
 *
 * ── Cap rule (red-team finding F8) ──────────────────────────────────────
 * Upstream worst case is 8-12 items; this must never become a wall of
 * bullets:
 *   - at most MAX_EXPANDED_ACTIONS action items render expanded/prominent;
 *     the rest move into the "Details" disclosure below.
 *   - ALL success items collapse into a single "Working boundaries: ..."
 *     line (still verbatim per-item text, just joined rather than one row
 *     each).
 *   - ALL info items live only inside the "Details" disclosure — never
 *     rendered inline.
 *
 * `dimension_key` is a machine key (e.g. "motion", "knee_valgus") and is
 * NEVER rendered as text — it is only used as a React key aid and exposed
 * via a `data-dimension-key` attribute as a test hook.
 *
 * Absent or empty `items` → renders null (legacy calibrations computed
 * before this field existed carry no `coach_feedback`).
 */

import type { ComponentType, SVGProps } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { Alert, CollapsibleSection } from '@/components/ui';
import { cn } from '@/utils/cn';
import type { CoachFeedbackItem, CoachFeedbackSeverity } from '@/types/calibrationTake';

interface CoachFeedbackListProps {
  items?: CoachFeedbackItem[] | null;
  /**
   * Sorted take ids `items` was computed from. Absent → treat as fresh
   * (legacy coach_feedback predating this field).
   */
  feedbackTakeIds?: string[] | null;
  /** Current take ids from the live take list, for the staleness comparison. */
  liveTakeIds?: string[];
}

/** At most this many action items render expanded/prominent (F8 cap rule). */
const MAX_EXPANDED_ACTIONS = 3;

const SEVERITY_META: Record<
  CoachFeedbackSeverity,
  {
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    iconClass: string;
    rowClass: string;
    textClass: string;
  }
> = {
  action: {
    Icon: ExclamationTriangleIcon,
    iconClass: 'text-warning',
    // Prominent — this is what the coach should do next.
    rowClass: 'bg-warning/10 rounded-md px-3 py-2',
    textClass: 'text-sm font-medium text-text-primary',
  },
  success: {
    Icon: CheckCircleIcon,
    iconClass: 'text-success',
    rowClass: 'px-0.5',
    textClass: 'text-sm text-text-secondary',
  },
  info: {
    Icon: InformationCircleIcon,
    iconClass: 'text-text-muted',
    rowClass: 'px-0.5',
    textClass: 'text-xs text-text-muted',
  },
};

function sortedEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function FeedbackRow({ item, overflow = false }: { item: CoachFeedbackItem; overflow?: boolean }) {
  const meta = SEVERITY_META[item.severity];
  const Icon = meta.Icon;
  return (
    <li
      data-testid="coach-feedback-item"
      data-severity={item.severity}
      data-dimension-key={item.dimension_key ?? undefined}
      data-overflow={overflow ? 'true' : undefined}
      className={cn('flex items-start gap-2', meta.rowClass)}
    >
      <Icon className={cn('h-4 w-4 flex-shrink-0 mt-0.5', meta.iconClass)} aria-hidden="true" />
      <span className={meta.textClass}>{item.text}</span>
    </li>
  );
}

export default function CoachFeedbackList({
  items,
  feedbackTakeIds,
  liveTakeIds = [],
}: CoachFeedbackListProps) {
  if (!items || items.length === 0) {
    return null;
  }

  // ── Staleness guard (F3) ────────────────────────────────────────────
  const isStale =
    feedbackTakeIds != null && !sortedEqual(feedbackTakeIds, liveTakeIds);

  if (isStale) {
    return (
      <div
        data-testid="coach-feedback-list"
        data-stale="true"
      >
        {/* Alert already sets role="alert" — used as the test hook instead
            of a data-testid (Alert does not forward arbitrary DOM attrs). */}
        <Alert variant="info">
          This feedback reflects an earlier set of example videos — recalibration
          hasn&apos;t completed since.
        </Alert>
      </div>
    );
  }

  // Group by severity, preserving each item's relative order within its group.
  const groups: Record<CoachFeedbackSeverity, CoachFeedbackItem[]> = {
    action: [],
    success: [],
    info: [],
  };
  for (const item of items) {
    if (groups[item.severity]) {
      groups[item.severity].push(item);
    }
  }

  // ── Cap rule (F8) ────────────────────────────────────────────────────
  const visibleActions = groups.action.slice(0, MAX_EXPANDED_ACTIONS);
  const overflowActions = groups.action.slice(MAX_EXPANDED_ACTIONS);
  const successItems = groups.success;
  const infoItems = groups.info;
  const disclosureCount = overflowActions.length + infoItems.length;

  return (
    <div
      className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-4 space-y-3"
      data-testid="coach-feedback-list"
    >
      {visibleActions.length > 0 && (
        <ul className="space-y-1.5" data-severity-group="action">
          {visibleActions.map((item, i) => (
            <FeedbackRow key={`action-${item.dimension_key ?? i}`} item={item} />
          ))}
        </ul>
      )}

      {/* All success items collapse into a single affirming line — still
          verbatim per-item text, just joined rather than one row each. */}
      {successItems.length > 0 && (
        <div
          className="flex items-start gap-2 px-0.5"
          data-testid="coach-feedback-success-summary"
          data-severity-group="success"
        >
          <CheckCircleIcon
            className="h-4 w-4 flex-shrink-0 mt-0.5 text-success"
            aria-hidden="true"
          />
          <span className="text-sm text-text-secondary">
            Working boundaries:{' '}
            {successItems.map((item, i) => (
              <span
                key={`success-${item.dimension_key ?? i}`}
                data-dimension-key={item.dimension_key ?? undefined}
              >
                {item.text}
                {i < successItems.length - 1 ? ', ' : ''}
              </span>
            ))}
          </span>
        </div>
      )}

      {/* Overflow action items (beyond MAX_EXPANDED_ACTIONS) + ALL info
          items live only here — never rendered inline (F8). */}
      {disclosureCount > 0 && (
        <CollapsibleSection
          title="Details"
          variant="default"
          defaultExpanded={false}
          badge={disclosureCount}
        >
          <ul className="space-y-1.5">
            {overflowActions.map((item, i) => (
              <FeedbackRow key={`action-overflow-${item.dimension_key ?? i}`} item={item} overflow />
            ))}
            {infoItems.map((item, i) => (
              <FeedbackRow key={`info-${item.dimension_key ?? i}`} item={item} />
            ))}
          </ul>
        </CollapsibleSection>
      )}
    </div>
  );
}
